import React, { useEffect, useState } from 'react';
import { Drawer, Form, Select, Input, Button, message, Spin, Card, Checkbox } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import * as Icons from '@ant-design/icons';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';
import pluralize from 'pluralize';
import eventBus from '../Components/eventBus';
import CustomDrawer from '../Components/CustomDrawer';

const { Option } = Select;

const CreateTabDrawer = ({
  visible,
  onClose,
  tabData,
  fetchTabsData,
  loading,
  setLoading,
  customComponents // 🔹 NEW PROP
}) => {
  const [form] = Form.useForm();

  const [objects, setObjects] = useState([]);
  const [fetchingObjects, setFetchingObjects] = useState(true);

  const [tabCategory, setTabCategory] = useState(null); // 🔹 NEW STATE

  // ================================
  // Fetch Tabs & Objects
  // ================================
  const fetchTabsAndObjects = async () => {
    try {
      setLoading(true);

      const tabsService = new ApiService(`${BASE_URL}/mt_tabs`, {
        'Content-Type': 'application/json',
      }, 'GET');
      const tabsResponse = await tabsService.makeCall();

      const objectsService = new ApiService(`${BASE_URL}/mt_objects`, {
        'Content-Type': 'application/json',
      }, 'GET');
      const objectsResponse = await objectsService.makeCall();

      const objectIdsInTabs = new Set(tabsResponse.map(tab => tab.mt_object_id));
      const filteredObjects = objectsResponse.filter(
        object => !objectIdsInTabs.has(object._id)
      );

      setObjects(filteredObjects);
    } catch (error) {
      const errorMessage =
        error && typeof error === 'object'
          ? Object.entries(error)
            .map(([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(', ') : value}`
            )
            .join(' | ')
          : 'Failed to fetch data';
      message.error(errorMessage);
    } finally {
      setFetchingObjects(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchTabsAndObjects();
    }
  }, [visible]);

  // ================================
  // Edit Mode Handling
  // ================================
  useEffect(() => {
    if (tabData) {
      form.setFieldsValue({
        tab_category: tabData.tab_category,
        label: tabData.label,
        name: tabData.name,
        object: tabData.mt_object_id,
        custom_component: tabData.custom_component,
        icon: tabData.icon,
        order: tabData.order,
        active: tabData.active,
        description: tabData.description,
      });

      setTabCategory(tabData.tab_category);
    } else {
      form.resetFields();
      setTabCategory(null);
    }
  }, [tabData, form]);

  // ================================
  // Icon Options
  // ================================
  const iconOptions = Object.keys(Icons)
    .filter(iconName => iconName[0] >= 'A' && iconName[0] <= 'T')
    .map(iconName => {
      const IconComponent = Icons[iconName];
      return (
        <Option key={iconName} value={iconName} label={iconName}>
          <span>
            {React.createElement(IconComponent)}
            <span style={{ marginLeft: 8 }}>{iconName}</span>
          </span>
        </Option>
      );
    });

  // ================================
  // Submit
  // ================================
  const handleSubmit = async (values) => {
    setLoading(true);

    const newTab = {
      label: values.label,
      name: values.name,
      description: values.description,
      icon: values.icon,
      order: values.order,
      active: values.active,
      tab_category: values.tab_category
    };

    if (values.tab_category === 'object') {
      newTab.mt_object_id = values.object;
    }

    if (values.tab_category === 'custom_component') {
      newTab.component_name = values.custom_component;
    }

    try {
      const apiServiceForTab = new ApiService(
        tabData ? `${BASE_URL}/mt_tabs/${tabData._id}` : `${BASE_URL}/mt_tabs`,
        { 'Content-Type': 'application/json' },
        tabData ? 'PUT' : 'POST',
        { mt_tab: newTab }
      );

      await apiServiceForTab.makeCall();

      fetchTabsAndObjects();
      fetchTabsData();

      message.success(tabData ? 'Tab updated successfully' : 'New tab created successfully');
      form.resetFields();
      onClose();
    } catch (error) {
      const errorMessage =
        error && typeof error === 'object'
          ? Object.entries(error)
            .map(([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(', ') : value}`
            )
            .join(' | ')
          : 'Failed to save tab';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ================================
  // API Name Generator
  // ================================
  const generateApiName = (label) =>
    label.replace(/[^a-zA-Z]/g, '').replace(/\s+/g, '').trim();

  const handleLabelChange = (e) => {
    form.setFieldsValue({
      name: generateApiName(e.target.value),
    });
  };

  return (
    <CustomDrawer
      title={tabData ? 'Edit Tab' : 'Create New Tab'}
      visible={visible}
      onClose={!loading ? onClose : null}
      closable={false}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onClose} >Cancel</Button>
          <Button type="primary" form="createTabForm" htmlType="submit" >
            Save
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        id="createTabForm"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ active: true }}
      >

        {/* 🔹 TAB CATEGORY */}
        <Form.Item
          label="Tab Category"
          name="tab_category"
          rules={[{ required: true, message: 'Please select tab category' }]}
        >
          <Select
            placeholder="Select tab category"
            onChange={(value) => {
              setTabCategory(value);
              form.setFieldsValue({ object: undefined, custom_component: undefined });
            }}
          >
            <Option value="object">Object</Option>
            <Option value="custom_component">Custom Component</Option>
          </Select>
        </Form.Item>

        {/* 🔹 OBJECT SELECT */}
        {tabCategory === 'object' && (
          <Form.Item
            label="Select Object"
            name="object"
            rules={[{ required: true, message: 'Please select an object' }]}
          >
            <Select loading={fetchingObjects} disabled={!!tabData}>
              {objects.map(object => (
                <Option key={object._id} value={object._id}>
                  {object.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* 🔹 CUSTOM COMPONENT SELECT */}
        {tabCategory === 'custom_component' && (
          <Form.Item
            label="Select Custom Component"
            name="custom_component"
            rules={[{ required: true, message: 'Please select a custom component' }]}
          >
            <Select placeholder="Select custom component">
              {customComponents.map(component => (
                <Option key={component} value={component}>
                  {component}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="label"
          label="Label"
          rules={[{ required: true, message: 'Please enter the label' }]}
        >
          <Input onBlur={handleLabelChange} />
        </Form.Item>

        <Form.Item
          name="name"
          label="API Name"
          rules={[
            { required: true, message: 'Please enter the name' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (!/^[a-zA-Z]+$/.test(value))
                  return Promise.reject(new Error('Only alphabets allowed'));
                if (pluralize.isPlural(value))
                  return Promise.reject(new Error('API name cannot be plural'));
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input disabled={!!tabData} />
        </Form.Item>

        <Form.Item name="active" label="Active" valuePropName="checked">
          <Checkbox />
        </Form.Item>

        <Form.Item
          name="order"
          label="Tab Order"
          rules={[{ required: true, message: 'Please enter tab order' }]}
        >
          <Input type="number" min={1} />
        </Form.Item>

        <Form.Item
          name="icon"
          label="Icon"
          rules={[{ required: true, message: 'Please select an icon' }]}
        >
          <Select optionLabelProp="label" showSearch>
            {iconOptions}
          </Select>
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea />
        </Form.Item>

      </Form>
    </CustomDrawer>
  );
};

export default CreateTabDrawer;
